import { Link } from '@tanstack/react-router';
import { MessageCircle } from 'lucide-react';
import { AvatarCircle } from '@/components/avatar-circle';
import { hasUnreadDiscussion } from '@/lib/discussions/discussion-unread';
import { discussionOtherParty } from '@/lib/discussions/types';
import { formatRelativeDate } from './format';
import type { DiscussionRow, ViewerRole } from './types';

/**
 * Carte Feed d'une discussion (migrations 0265-0267) — calque structurel
 * `attempt-card.tsx` (léger, pas de vignette). Toute la carte est cliquable :
 * seul comportement demandé, ouvrir le fil. `DiscussionRow` porte déjà le nom
 * de l'AUTRE participant (join côté serveur) — pas besoin d'`authorsById`.
 */
export function DiscussionCard({
  data,
  viewerRole,
}: {
  data: DiscussionRow;
  viewerRole: ViewerRole;
}) {
  const myRole: 'tutor' | 'student' = viewerRole === 'tutor' ? 'tutor' : 'student';
  const { name: otherName, avatarUrl: otherAvatar } = discussionOtherParty(data, myRole);
  const unread = hasUnreadDiscussion(data, myRole);

  return (
    <Link
      to="/discussions/$discussionId"
      params={{ discussionId: data.id }}
      className="block overflow-hidden rounded-2xl bg-white active:scale-[0.99]"
    >
      <div className="flex items-center gap-2.5 px-4 py-3">
        <AvatarCircle name={otherName} src={otherAvatar} size="sm" />
        <div className="min-w-0 flex-1">
          {unread ? (
            <>
              <span className="text-ink-900 font-sans text-xs font-semibold">{otherName}</span>
              <span className="text-ink-500 font-sans text-xs"> t’a écrit</span>
            </>
          ) : (
            <>
              <span className="text-ink-900 font-sans text-xs font-semibold">Tu</span>
              <span className="text-ink-500 font-sans text-xs"> as échangé avec {otherName}</span>
            </>
          )}
        </div>
        <span className="text-ink-400 shrink-0 font-sans text-[11px]">
          {formatRelativeDate(data.lastMessageAt)}
        </span>
      </div>

      <div className="flex items-center gap-3 px-4 pb-4">
        <div className="border-primary-300 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2">
          <MessageCircle className="text-primary-500 h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-1.5">
            <span className="text-ink-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
              Discussion
            </span>
            {unread && (
              <span
                aria-label="Non lu"
                className="bg-primary-400 inline-block size-2 shrink-0 rounded-full"
              />
            )}
          </div>
          <p
            className={[
              'text-ink-900 truncate font-sans text-sm leading-snug',
              unread ? 'font-bold' : 'font-semibold',
            ].join(' ')}
          >
            {data.title}
          </p>
        </div>
      </div>
    </Link>
  );
}
